import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, ArrowRight, Gift, Send, Store } from "lucide-react";
import { api, euro, img } from "@/lib/api";
import { Spinner } from "@/components/Layout";

const prezzoDa = (p) => Math.min(...[...(p.tagli_cent || []), p.importo_libero ? p.importo_min_cent : Infinity]);

const OperatoreCard = ({ o, i }) => {
  const p = o.vetrina.prodotto;
  const cover = o.vetrina.immagini?.find((x) => x.slot === 1);
  return (
    <Link to={`/vetrina/${o.id}`} data-testid={`catalog-card-${o.id}`} className="group fade-up block" style={{ animationDelay: `${i * 60}ms` }}>
      <div className="aspect-[4/3] overflow-hidden rounded-2xl border border-[#E3D9F0] bg-[#EEE7F6]">
        {cover && <img src={img(cover.path_400)} alt={o.ragione_sociale} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />}
      </div>
      <div className="mt-4 flex items-start justify-between gap-3">
        <div>
          <p className="caption">{o.categoria}</p>
          <h3 className="font-display text-2xl leading-tight mt-1 group-hover:text-[#7E5BAF] transition-colors">{o.ragione_sociale}</h3>
          <p className="text-sm text-[#786E65] flex items-center gap-1 mt-1"><MapPin size={14} />{o.citta}</p>
        </div>
        <span className="text-sm whitespace-nowrap text-[#786E65]">da <strong className="text-[#261E1B]">{euro(prezzoDa(p))}</strong></span>
      </div>
    </Link>
  );
};

const Passi = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-24 grid md:grid-cols-3 gap-10">
    {[[Store, "Scegli l'attività", "Ristoranti, centri benessere, botteghe ed esperienze della tua città."],
      [Gift, "Personalizza", "Importo, quantità e un messaggio di auguri fino a 300 caratteri."],
      [Send, "Regala", "Consegna immediata o programmata via email, con PDF e QR da mostrare in negozio."]].map(([Icon, t, d], i) => (
      <div key={t} className="border-t border-[#E3D9F0] pt-6">
        <span className="font-display text-5xl text-[#C8A2E8]">0{i + 1}</span>
        <h3 className="font-display text-2xl mt-2 flex items-center gap-2"><Icon size={20} className="text-[#7E5BAF]" />{t}</h3>
        <p className="text-[#786E65] mt-2">{d}</p>
      </div>
    ))}
  </section>
);

export default function Home() {
  const [items, setItems] = useState(null);
  const [categorie, setCategorie] = useState([]);
  const [citta, setCitta] = useState([]);
  const [f, setF] = useState({ q: "", categoria: "", citta: "" });

  useEffect(() => {
    api.get("/auth/categorie").then((r) => setCategorie(r.data));
    api.get("/pubblico/citta").then((r) => setCitta(r.data));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      api.get("/pubblico/catalogo", { params }).then((r) => setItems(r.data));
    }, 250);
    return () => clearTimeout(t);
  }, [f]);

  return (
    <div>
      <section className="relative grain overflow-hidden bg-gradient-to-b from-[#F1EAF8] to-[#FAF7FD] border-b border-[#E3D9F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <p className="caption">Gift card delle attività italiane</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-4">
              Il regalo giusto <em className="text-[#7E5BAF]">a due passi</em> da chi ami.
            </h1>
            <p className="text-base md:text-lg text-[#786E65] mt-6 max-w-xl">Scegli tra le vetrine delle attività locali, componi la tua gift card e inviala subito o nel giorno speciale.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#catalogo" className="btn-primary" data-testid="hero-cta-catalogo">Esplora il catalogo <ArrowRight size={18} /></a>
              <Link to="/verifica-saldo" className="btn-secondary" data-testid="hero-cta-saldo">Ho già una gift card</Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: "150ms" }}>
            <img src="/hero.jpg" alt="Pacchetto regalo" className="rounded-[2rem] border border-[#E3D9F0] shadow-xl aspect-[4/5] object-cover w-full max-h-[520px]" />
            <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-[#FFFEFF] border border-[#E3D9F0] rounded-2xl px-5 py-4 shadow-lg">
              <p className="caption">Gift card</p>
              <p className="font-display text-3xl text-[#7E5BAF]">€ 50,00</p>
              <p className="font-code text-xs text-[#786E65]">XXXX-XXXX-XXXX-7K2P</p>
            </div>
          </div>
        </div>
      </section>

      <section id="catalogo" className="max-w-7xl mx-auto px-4 sm:px-6 pt-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <p className="caption">Catalogo</p>
            <h2 className="font-display text-3xl sm:text-4xl mt-2">Attività con vetrina attiva</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full md:w-auto md:min-w-[620px]">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0958B]" />
              <input className="field pl-9" placeholder="Cerca per nome" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="search-input" />
            </div>
            <select className="field" value={f.categoria} onChange={(e) => setF({ ...f, categoria: e.target.value })} data-testid="filter-categoria">
              <option value="">Tutte le categorie</option>
              {categorie.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select className="field" value={f.citta} onChange={(e) => setF({ ...f, citta: e.target.value })} data-testid="filter-citta">
              <option value="">Tutte le città</option>
              {citta.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>
        {items === null ? <Spinner /> : items.length === 0 ? (
          <div className="card mt-10 text-center py-16" data-testid="catalog-empty">
            <p className="font-display text-2xl">Nessuna attività trovata</p>
            <p className="text-[#786E65] mt-2">Prova a cambiare categoria o città.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12 mt-10" data-testid="catalog-grid">
            {items.map((o, i) => <OperatoreCard key={o.id} o={o} i={i} />)}
          </div>
        )}
      </section>
      <Passi />
    </div>
  );
}
